import { useRef, useEffect } from 'react';

import { useBleReader } from 'src/hooks/useBleReader';

import useUserDataStore from 'src/store/user';
import { CONFIG } from 'src/environments/environment';
import { useSystemStore } from 'src/store/SystemStore';
import { DeviceType } from 'src/store/SystemStore/types';

import SelectBusinessDeviceDialog from './select-business-device-dialog';

type Props = {
  children: React.ReactNode;
};

export default function SelectCurrentDevice({ children }: Props) {
  const { currentDevice, currentDeviceType, usesBleReader } = useSystemStore();
  const { business } = useUserDataStore();
  const {
    initializeTerminal,
    checkConnectedReader,
    ensureReaderConnected
  } = useBleReader();

  // Track if we've attempted connection to prevent multiple attempts
  const connectionAttempted = useRef(false);

  // Auto-connect BLE reader when conditions are met
  useEffect(() => {
    const connectBleReaderInBackground = async () => {
      // Check all conditions for auto-connection
      if (
        currentDevice.initialized &&  // Device is selected
        usesBleReader &&              // BLE reader is enabled in settings
        currentDeviceType === DeviceType.MOBILE &&  // Only on mobile devices
        !connectionAttempted.current  // Haven't attempted yet
      ) {
        connectionAttempted.current = true;

        try {
          console.log('BLE Reader: Auto-connection initiated in background');

          // First, initialize the terminal if needed
          const initialized = await initializeTerminal();
          if (!initialized) {
            console.log('BLE Reader: Terminal initialization failed, skipping auto-connection');
            return;
          }

          // Check if already connected (cached reader)
          const existingReader = await checkConnectedReader();
          if (existingReader) {
            console.log('BLE Reader: Already connected to reader:', existingReader.serialNumber);
            return;
          }

          // Get location ID from environment config
          const locationId = CONFIG.VITE_DEFAULT_TERMINAL_READER_LOCATION_ID;
          if (!locationId) {
            console.warn('BLE Reader: No location ID configured, skipping auto-connection');
            return;
          }

          // Attempt to connect in background (non-blocking)
          // Using setTimeout to ensure this doesn't block the UI
          setTimeout(async () => {
            try {
              console.log('BLE Reader: Attempting auto-discovery and connection...');
              const connected = await ensureReaderConnected(locationId);

              if (connected) {
                console.log('BLE Reader: Successfully auto-connected to reader');
              } else {
                console.log('BLE Reader: Auto-connection failed, reader will connect on first payment');
              }
            } catch (error) {
              console.error('BLE Reader: Auto-connection error:', error);
              // Don't throw - this is a background operation
            }
          }, 1000); // Small delay to ensure UI is ready

        } catch (error) {
          console.error('BLE Reader: Failed to initiate auto-connection:', error);
          // Don't throw - this is a background operation that shouldn't block the app
        }
      }
    };

    connectBleReaderInBackground();
  }, [
    currentDevice.initialized,
    usesBleReader,
    currentDeviceType,
    initializeTerminal,
    checkConnectedReader,
    ensureReaderConnected
  ]);

  // Reset connection attempt flag when device changes
  useEffect(() => {
    if (!currentDevice.initialized) {
      connectionAttempted.current = false;
    }
  }, [currentDevice.initialized]);

  if (!currentDevice.initialized) {
    console.log('[SelectCurrentDevice] Device not initialized, showing selection dialog');
    return (
      <div>
        <SelectBusinessDeviceDialog
          business={business}
          open
          onClose={() => {
            // The dialog handles setting currentDevice.initialized = true internally
            // This callback is for when dialog is dismissed without selection
            console.log('[SelectCurrentDevice] Dialog closed');
          }}
        />
      </div>
    );
  }

  return <>{children}</>;
}
